import React, { useEffect, useRef } from 'react';
import classnames from 'classnames/bind';
import { Button, CloseIcon } from 'tuui';
import { Collapsible, remToPx } from 'taboola-ultimate-ui';
import { useChatAgentPermitted } from 'components/ChatAgent/hooks/useChatAgentPermitted';
import { ReactComponent as ChatAgentIcon } from 'modules/onboarding/OnboardingChatForm/components/InitWidget/welcome-message-icon.svg';
import {
    COLLAPSED_WIDTH,
    EXPANDED_WIDTH as EXPANDED_WIDTH_WITH_PADDING,
} from 'modules/taboola-common-frontend-modules/chat-agent/config/chatAgentSideBarConfig';
import { useChatAgentContext } from 'modules/taboola-common-frontend-modules/chat-agent/hooks/useChatAgentContext';
import { FormattedMessage } from 'modules/taboola-common-frontend-modules/i18n';
import { useModuleName } from '../../hooks';
import { MODULE_NAME } from '../../modules/onboarding';
import { ChatDevTool } from '../ChatAgent/components/ChatAgentDevTool/ChatDevTool';
import styles from './chatAgentSideBar.module.scss';

const classNameBuilder = classnames.bind(styles);

const transitionDuration = 1000 * Number.parseFloat(styles.animationTime);

const PADDING_LEFT = remToPx(styles.paddingLeft).replace('px', '');
const EXPANDED_WIDTH = EXPANDED_WIDTH_WITH_PADDING - PADDING_LEFT;

export const ChatAgentSideBar = () => {
    const moduleName = useModuleName();
    const isOnboardingModule = moduleName === MODULE_NAME;
    const { isCollapsed: collapsed, closeChat, initializeChat, isChatOpened } = useChatAgentContext();
    const isPermitted = useChatAgentPermitted();
    const chatContainerRef = useRef();
    const containerRendered = !!chatContainerRef.current;

    useEffect(() => {
        if (containerRendered && chatContainerRef.current && !isChatOpened) {
            initializeChat({
                container: chatContainerRef.current,
            });
        }
    }, [containerRendered, initializeChat, isChatOpened]);

    if (!isPermitted || isOnboardingModule) {
        return null;
    }

    return (
        <div className={classNameBuilder('container', { collapsed })}>
            <Collapsible
                collapsed={collapsed}
                direction={Collapsible.DIRECTION.HORIZONTAL}
                collapsedSize={COLLAPSED_WIDTH}
                expandedSize={EXPANDED_WIDTH}
                className={styles['collapsible']}
                transitionDuration={transitionDuration}
            >
                <div className={styles['collapsible-content']} style={{ width: EXPANDED_WIDTH }}>
                    <div className={styles['header']}>
                        <div className={styles['title']}>
                            <ChatAgentIcon className={styles['chat-agent-icon']} />
                            <FormattedMessage id="chat.agent.title" defaultMessage="Abby AI" />
                        </div>
                        <div className={styles['action-buttons-container']}>
                            <Button
                                size={Button.size.md}
                                variant={Button.variant.text}
                                onClick={() => closeChat()}
                                className={styles['close-button']}
                                aria-label="Close"
                            >
                                <CloseIcon className={styles['close-icon']} />
                            </Button>
                        </div>
                    </div>
                    <div className={classNameBuilder('chat-container')} ref={chatContainerRef} />
                    <ChatDevTool />
                </div>
            </Collapsible>
        </div>
    );
};
